/**
 * WEB-2054 — виды блоков, встречающиеся в ГОТОВОМ теле показа.
 *
 * ЗАЧЕМ. Ответ резолва (`ResolvedHostTarget.publicContent`, WEB-1989) приносит
 * тело воронки ДО первой отрисовки. Значит браузер может начать качать куски
 * рендерера ПАРАЛЛЕЛЬНО ядру, а не после того, как первый редкий блок дойдёт до
 * отрисовки. Это вторая половина разреза: без неё редкий блок ждал бы свой
 * кусок на глазах у посетителя.
 *
 * ПОЧЕМУ ОБЩИЙ ОБХОД, А НЕ ЗАШИТЫЙ ПУТЬ `screens[].blocks[].type`. Тело приходит
 * нетипизированным (`Record<string, unknown>`) и разной формы: у квиза экраны
 * лежат рядом с воронкой, у пейволла — внутри своего объекта, а копия воркера на
 * краю отдаёт то, что ей когда-то положили. Зашитый путь перестал бы находить
 * блоки молча — грелка просто выключилась бы, и никто бы не заметил. Обход же
 * берёт ТОЛЬКО те значения поля `type`, которые есть в каталоге видов: чужие
 * `type` (вид воронки, вид направления) в каталог не входят и кусков не просят.
 *
 * Границы обхода: посещённые объекты помнятся (кольцевые ссылки), число узлов
 * ограничено — тело приходит из сети, и обход по нему не имеет права стать
 * платой за первый экран.
 */
import {
  CORE_BLOCK_TYPES,
  EXTENDED_BLOCK_TYPES,
  PORTAL_BLOCK_TYPES,
  REFERENCE_BLOCK_TYPES,
} from './publicBlockChunkMap';

const KNOWN_BLOCK_TYPES: ReadonlySet<string> = new Set<string>([
  ...CORE_BLOCK_TYPES,
  ...EXTENDED_BLOCK_TYPES,
  ...PORTAL_BLOCK_TYPES,
  ...REFERENCE_BLOCK_TYPES,
]);

/**
 * Потолок обхода: воронка в сотни блоков сюда укладывается с запасом.
 *
 * Починка ревизии: считаются ТОЛЬКО пройденные узлы — объекты и массивы. Пока
 * потолок тратился на каждое значение подряд, его съедали поля блоков (у одного
 * блока их десятки: тексты, цвета, размеры), и до вложенного содержимого в
 * конце большой воронки обход не доходил — грелка молча переставала видеть её
 * хвост, и никто бы об этом не узнал.
 */
const MAX_VISITED_NODES = 20000;

/**
 * Достигнутый потолок — событие, а не норма: значит тело показа выросло больше
 * ожидаемого, и часть видов блоков грелка НЕ увидела. Строка техническая (в
 * консоль браузера, для разбора), человеку её не показывают — пары словарей ей
 * не нужно.
 */
const TRAVERSAL_BUDGET_MESSAGE =
  '[public-content] block-type scan stopped at the node budget; some chunks may load late';

/**
 * Виды блоков из тела показа, в порядке первой встречи. Чужое или пустое тело
 * даёт пустой список — это не ошибка, а «греть нечего».
 */
export function collectPublicContentBlockTypes(content: unknown): string[] {
  const found: string[] = [];
  const seen = new Set<string>();
  const visited = new Set<object>();
  const queue: unknown[] = [content];
  let budget = MAX_VISITED_NODES;

  while (queue.length > 0) {
    const node = queue.shift();
    if (node === null || typeof node !== 'object') continue;
    if (visited.has(node)) continue;
    // Тратим потолок ровно на то, по чему идём: на узел, а не на его поля.
    if (budget <= 0) {
      console.warn(TRAVERSAL_BUDGET_MESSAGE);
      break;
    }
    budget -= 1;
    visited.add(node);

    if (Array.isArray(node)) {
      queue.push(...node);
      continue;
    }

    const record = node as Record<string, unknown>;
    const type = record['type'];
    if (
      typeof type === 'string' &&
      KNOWN_BLOCK_TYPES.has(type) &&
      !seen.has(type)
    ) {
      seen.add(type);
      found.push(type);
    }
    for (const value of Object.values(record)) queue.push(value);
  }

  return found;
}
